/** @jsx jsx */
import { jsx, Flex, Box, Styled, useThemeUI } from "theme-ui"
import { alpha } from "@theme-ui/color"
import { graphql, useStaticQuery } from "gatsby"
import BackgroundImage from "gatsby-background-image"

import { Container } from "components/layout"
import Panel from "components/panel"
import LocalizedButton from "components/localized-button"

import Kite from "components/svgs/kite"
import Twist from "components/svgs/twist"

import useLocale from "hooks/use-locales"

const SupportingEachother = () => {
  const { theme } = useThemeUI()
  const { t } = useLocale()
  const data = useStaticQuery(graphql`
    query {
      left: file(relativePath: { eq: "home-supporting-eachother-1.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 1920) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      right: file(relativePath: { eq: "home-supporting-eachother-2.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 1920) {
            ...GatsbyImageSharpFluid
          }
        }
      }
    }
  `)
  const styles = {
    heading: { color: "background", textAlign: "center", mt: 0 },
    intro: { textAlign: "center", mb: 5 },
    wrapper: { flexDirection: ["column", "row"], alignItems: "flex-start" },
    column: { width: ["100%", "50%"] },
    background: {
      backgroundPosition: "center",
      backgroundSize: "cover",
      pt: 0,
    },
    inner: {
      flexDirection: ["column", "column", "row"],
      alignItems: "center",
      backgroundImage: t => `
      linear-gradient(
        to top,
        ${alpha("secondary", 1)(t)},
        ${alpha("secondary", 0.85)(t)},
        ${alpha("secondary", 0)(t)}
      )
    `,
      textAlign: "center",
      pt: 7,
      pb: 4,
    },
    iconWrapper: {
      width: [0, 0, "20%"],
      px: 3,
      display: ["none", "block"],
    },
    icon: { height: "auto", maxWidth: "100%" },
    contentWrapper: {
      width: ["100%", "100%", "75%"],
      px: 4,
      pt: 5,
      textAlign: "center",
    },
  }

  return (
    <Panel bg="secondary" color="background" space={[4, 5]}>
      <Container>
        <Styled.h1 as="h2" sx={styles.heading}>
          {t("support.title")}
        </Styled.h1>
        <Styled.p sx={styles.intro}>{t("support.intro")}</Styled.p>
      </Container>
      <Flex sx={styles.wrapper}>
        <Box sx={styles.column}>
          <BackgroundImage
            Tag="div"
            fluid={data.left.childImageSharp.fluid}
            backgroundColor={theme.colors.secondary}
            sx={styles.background}
          >
            <Flex sx={styles.inner}>
              <Box sx={styles.iconWrapper}>
                <Twist sx={styles.icon} />
              </Box>
              <Box sx={styles.contentWrapper}>
                <Styled.h3>{t("support.one.title")}</Styled.h3>
                <Styled.p>{t("support.one.intro")}</Styled.p>
                <Styled.p>
                  <LocalizedButton to="/supporting-eachother-1">
                    {t("support.one.button")}
                  </LocalizedButton>
                </Styled.p>
              </Box>
            </Flex>
          </BackgroundImage>
        </Box>
        <Box sx={styles.column}>
          <BackgroundImage
            Tag="div"
            fluid={data.right.childImageSharp.fluid}
            backgroundColor={theme.colors.secondary}
            sx={styles.background}
          >
            <Flex sx={styles.inner}>
              <Box sx={styles.contentWrapper}>
                <Styled.h3>{t("support.two.title")}</Styled.h3>
                <Styled.p>{t("support.two.intro")}</Styled.p>
                <Styled.p>
                  <LocalizedButton to="/supporting-eachother-2">
                    {t("support.two.button")}
                  </LocalizedButton>
                </Styled.p>
              </Box>
              <Box sx={styles.iconWrapper}>
                <Kite sx={styles.icon} />
              </Box>
            </Flex>
          </BackgroundImage>
        </Box>
      </Flex>
    </Panel>
  )
}

export default SupportingEachother
